// The browser side of a pane that works the page through ui_search and
// ui_call (#1534): run the model's calls here, give it the results, ask
// again, until it answers without one. The Tables Insights pane
// (hooks/useTableChat) and the Help pane (hooks/useHelpChat) both run it, so
// the confirm rules are one set of code: an `ask` tool's confirmed=true
// stands only after the user said yes to that ask (confirmedByUser), a
// `click` tool waits for the pane's Confirm (the bridge drops a model's own
// confirmed), and a reply that invents a Confirm button is asked again once.

import type { ChatMessage, ToolCall } from './types'
import { isBridgeTool, UI_CALL_TOOL } from './uiCalls'
import { callArgs, type UIBridge } from '../../voice/uiBridge'

/** The most model round trips one message may take while it works the page. */
export const MAX_UI_ROUNDS = 6

// A reply that tells the user to click a button (Confirm, Rename…).
const CLICK_CLAIM = /\b(click|press|tap)\b[^.?!]*\b(confirm|button)\b|\bconfirm button\b/i

/**
 * True when the turn's last reply tells the user to click a button but no
 * tool result in this turn asked for one: the model described a change it
 * never requested, so there is no button to click (#1534).
 */
export function claimsUnaskedConfirmation(conversation: ChatMessage[]): boolean {
  const last = conversation[conversation.length - 1]
  if (last?.role !== 'assistant' || last.tool_calls?.length) return false
  if (typeof last.content !== 'string' || !CLICK_CLAIM.test(last.content)) return false
  let start = conversation.length - 1
  while (start > 0 && conversation[start].role !== 'user') start--
  return !conversation.slice(start).some(m => {
    if (m.role !== 'tool' || typeof m.content !== 'string') return false
    try {
      return !!JSON.parse(m.content)?.needs_confirmation
    } catch {
      return false
    }
  })
}

const parseObject = (s: string | undefined): Record<string, unknown> | null => {
  try {
    const v = JSON.parse(s ?? '')
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

// The page tool a call runs: ui_call's `tool`, or the call itself.
function calledTool(call: ToolCall): string {
  if (call.function.name !== UI_CALL_TOOL) return call.function.name
  const tool = parseObject(call.function.arguments)?.tool
  return typeof tool === 'string' ? tool : ''
}

/**
 * Whether the user agreed to this `ask` tool call (`confirm: 'ask'`, the
 * quick filter). True only when the latest `ask_user` answer from that tool
 * was about the same filter and the user has written since. A model's
 * confirmed=true without that is dropped, and the tool asks again: the model
 * can neither confirm its own proposal in the same turn nor carry a yes over
 * to a different filter.
 */
export function confirmedByUser(conversation: ChatMessage[], toolName: string, args: Record<string, unknown>): boolean {
  const lastUser = conversation.map(m => m.role).lastIndexOf('user')
  const callsToTool = new Set<string>()
  let askedAt = -1
  let askedFilter: unknown
  for (const [at, m] of conversation.entries()) {
    if (m.role === 'assistant') {
      for (const call of m.tool_calls ?? []) if (calledTool(call) === toolName) callsToTool.add(call.id)
    }
    if (m.role === 'tool' && m.tool_call_id && callsToTool.has(m.tool_call_id)) {
      const result = parseObject(m.content)
      if (result?.ask_user) {
        askedAt = at
        askedFilter = result.filter
      }
    }
  }
  return askedAt >= 0 && askedAt < lastUser && askedFilter === String(args.filter ?? '').trim()
}

/** Sent once, with that request only, when a reply invented a Confirm button. */
export const UNASKED_CONFIRMATION_NOTE =
  'Correction: your last reply told the user to click a button, but no tool asked for one, so there is no button. ' +
  'If the user asked for a change on the page, call ui_search and then ui_call now; a button appears only when the tool result says needs_confirmation. ' +
  'If no tool can do it, say so plainly and do not mention a button.'

export interface UiToolLoopOptions {
  /** The page's tools, reached as ui_search / ui_call. */
  bridge: UIBridge
  /** One model round trip. `note` rides with that request only. */
  postChat: (conversation: ChatMessage[], note?: string) => Promise<ChatMessage[]>
  /** Runs after each round trip: Tables re-asks once for a chart that will not draw. */
  afterPost?: (conversation: ChatMessage[]) => Promise<ChatMessage[]>
  /** Sees each step, so the pane shows what happened and a failure keeps what was done. */
  progress: (conversation: ChatMessage[]) => void
  /** The ids of the calls this pane ran: only those may be confirmed. */
  onCallsRun?: (callIds: string[]) => void
}

/**
 * Run the model's ui_search / ui_call calls in the browser, give it the
 * results and ask again, until it answers without one. Bounded: each round
 * is a model call.
 */
export async function runUiToolLoop(conversation: ChatMessage[], options: UiToolLoopOptions): Promise<ChatMessage[]> {
  const { bridge, postChat, progress, onCallsRun } = options
  const afterPost = options.afterPost ?? (async (c: ChatMessage[]) => c)
  const bridgeCalls = (c: ChatMessage[]) => {
    const last = c[c.length - 1]
    return last?.role === 'assistant' ? (last.tool_calls ?? []).filter(tc => isBridgeTool(tc.function.name)) : []
  }
  let convo = conversation
  let corrected = false
  for (let round = 0; round < MAX_UI_ROUNDS; round++) {
    const calls = bridgeCalls(convo)
    if (calls.length === 0) {
      // Once per message: a reply that points at a button no tool asked
      // for is dropped and asked again, with a note sent for that request only.
      if (corrected || !claimsUnaskedConfirmation(convo)) return convo
      corrected = true
      convo = await afterPost(await postChat(convo.slice(0, -1), UNASKED_CONFIRMATION_NOTE))
      progress(convo)
      continue
    }
    const rows: ChatMessage[] = []
    for (const call of calls) {
      let args: Record<string, unknown> = {}
      try {
        args = JSON.parse(call.function.arguments || '{}')
      } catch {
        rows.push({ role: 'tool', tool_call_id: call.id, content: JSON.stringify({ error: 'the arguments were not valid JSON' }) })
        continue
      }
      // An ask tool's confirmed=true stands only when the user said yes to
      // that ask (confirmedByUser); otherwise the tool asks again.
      if (call.function.name === UI_CALL_TOOL) {
        const toolArgs = callArgs(args.args)
        const name = String(args.tool ?? '')
        if (toolArgs.confirmed === true && bridge.find(name)?.meta?.confirm === 'ask' && !confirmedByUser(convo, name, toolArgs)) {
          delete toolArgs.confirmed
          args = { ...args, args: toolArgs }
        }
      }
      const content = await bridge.execute(call.function.name, args)
      rows.push({ role: 'tool', tool_call_id: call.id, content })
    }
    onCallsRun?.(calls.map(c => c.id))
    convo = [...convo, ...rows]
    progress(convo)
    convo = await afterPost(await postChat(convo))
    progress(convo)
  }
  // Out of rounds: answer what is left so the conversation stays valid.
  const left = bridgeCalls(convo)
  if (left.length === 0) return convo
  return [
    ...convo,
    ...left.map(call => ({ role: 'tool', tool_call_id: call.id, content: JSON.stringify({ error: 'not run: too many steps in one message' }) })),
    { role: 'assistant', content: `I stopped after ${MAX_UI_ROUNDS} steps. Tell me what to try next.` },
  ]
}
